/**
 * Storefront-only display labels for video-game variants.
 *
 * The internal POS uses the trade-in terminology (New / CIB / Loose),
 * but casual shoppers don't always recognise "CIB" or "Loose", so on the
 * customer-facing storefront we relabel them as "New" / "Used" with a
 * descriptive subtitle. Pure presentational — the underlying variant
 * data sent to Shopify is unchanged.
 */
const TITLE_MAP = {
  cib:   { label: 'Used', subtitle: 'Includes item, original packaging, manuals, inserts, etc (if applicable)' },
  loose: { label: 'Used', subtitle: 'Includes item, but may have reprinted cover art, be cart only, without manual, box inserts, etc' },
};

/**
 * TCG condition abbreviations → full, shopper-friendly names. Casual buyers
 * don't always know "NM" means Near Mint, so we expand any whole-word
 * abbreviation found in a variant title (e.g. "Holo NM" → "Holo Near Mint",
 * "1st Edition Non-Foil LP" → "1st Edition Non-Foil Lightly Played").
 */
const CONDITION_ABBR = {
  nm:  'Near Mint',
  lp:  'Lightly Played',
  mp:  'Moderately Played',
  hp:  'Heavily Played',
  dmg: 'Damaged',
  dm:  'Damaged',
};

export function expandConditionAbbreviations(text) {
  if (!text) return text;
  // `dmg` before `dm` so "DMG" maps to Damaged (word boundary already keeps
  // them distinct, but ordering is safest). Case-insensitive, whole-word only.
  return text.replace(/\b(nm|lp|mp|hp|dmg|dm)\b/gi, (m) => CONDITION_ABBR[m.toLowerCase()] || m);
}

/**
 * Given a raw variant title (e.g. "CIB", "Loose", "1st Edition Non-Foil NM"),
 * return the customer-facing { label, subtitle } pair.
 *
 *   - Video-game variant matches: relabelled with a friendly subtitle.
 *   - TCG conditions: abbreviations expanded to full names.
 *   - Everything else passes through unchanged with `subtitle: null`.
 */
export function getVariantDisplay(variantTitle) {
  const raw = (variantTitle || '').trim();
  const key = raw.toLowerCase();
  if (TITLE_MAP[key]) return TITLE_MAP[key];
  return { label: expandConditionAbbreviations(raw), subtitle: null };
}
